import axios from "axios";

/**
 * Shared HTTP client.
 *
 * - withCredentials: the JWT lives in an HttpOnly cookie the browser manages
 *   automatically; JS never sees the token.
 * - timeout: a hard 15s ceiling so a stuck request eventually surfaces as
 *   an error instead of a spinner that never resolves.
 * - 401: we optimistically clear the UI's "authenticated" hint and bounce
 *   to /login. The server already invalidated the cookie, we just re-sync.
 *
 * Callers can opt out of the global network-error toast by setting
 * `config.silent = true` on the request; useful for polling / pre-flights
 * that shouldn't spam the user.
 */

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/api",
  withCredentials: true,
  timeout: 15000,
});

// External hook: ToastProvider calls registerErrorToaster(showToast) on
// mount so the interceptor can surface network/5xx errors without the
// frontend components having to wrap every api.get() in try/catch boilerplate.
let toaster = null;
export function registerErrorToaster(fn) {
  toaster = fn;
}

function isNetworkOrTimeout(error) {
  return (
    error.code === "ECONNABORTED" ||
    error.code === "ERR_NETWORK" ||
    error.message === "Network Error" ||
    !error.response
  );
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const silent = error.config?.silent === true;

    if (status === 401) {
      const onLoginPage = window.location.pathname.startsWith("/login");
      localStorage.removeItem("authenticated");
      localStorage.removeItem("email");
      localStorage.removeItem("placeName");
      localStorage.removeItem("placeSlug");
      localStorage.removeItem("role");
      if (!onLoginPage) {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }

    if (!silent && toaster) {
      if (isNetworkOrTimeout(error)) {
        toaster("Сеть недоступна — проверьте подключение", { type: "error" });
      } else if (status >= 500) {
        toaster("Сервис временно недоступен. Попробуйте ещё раз.", { type: "error" });
      }
      // 4xx (besides 401) are surfaced by the calling component — they are
      // business errors like "email already used" that need contextual text.
    }

    return Promise.reject(error);
  }
);

export default api;
